const REQUIRED_FIREBASE_ANALYTICS_ENV_KEYS = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID",
  "VITE_FIREBASE_MEASUREMENT_ID",
] as const;

type AnalyticsEnvLogger = Pick<Console, "warn" | "error">;

export type AnalyticsEnvValidationResult = {
  missingKeys: string[];
  adsTagId: string | null;
  warnsAboutMissingMeasurementForAds: boolean;
};

export function collectMissingFirebaseAnalyticsEnvKeys(
  env: Record<string, string | undefined>
): string[] {
  return REQUIRED_FIREBASE_ANALYTICS_ENV_KEYS.filter((key) => !env[key]?.trim());
}

export function validateAnalyticsEnvironment(options?: {
  isProd?: boolean;
  env?: Record<string, string | undefined>;
  logger?: AnalyticsEnvLogger;
}): AnalyticsEnvValidationResult | null {
  const isProd = options?.isProd ?? import.meta.env.PROD;
  if (!isProd) {
    return null;
  }

  const env = options?.env ?? (import.meta.env as Record<string, string | undefined>);
  const logger = options?.logger ?? console;
  const adsTagId = env.VITE_GOOGLE_ADS_TAG_ID?.trim() ?? null;
  const missingKeys = collectMissingFirebaseAnalyticsEnvKeys(env);
  const warnsAboutMissingMeasurementForAds =
    Boolean(adsTagId) && missingKeys.includes("VITE_FIREBASE_MEASUREMENT_ID");

  if (missingKeys.length === 0) {
    return {
      missingKeys,
      adsTagId,
      warnsAboutMissingMeasurementForAds,
    };
  }

  if (warnsAboutMissingMeasurementForAds && adsTagId) {
    logger.warn(
      "[analytics] Google Ads tag configured without GA4 measurement ID; Ads tracking depends on unified GA runtime",
      {
        adsTagId,
      }
    );
  }

  // Staging deliberately ships a blank VITE_FIREBASE_MEASUREMENT_ID (its Firebase
  // project has no GA4 property), so downgrade to a warning there instead of a
  // permanent console error. Anything else — including an unset/unrecognized
  // target — stays on the loud error path: an unset VITE_DEPLOY_TARGET is exactly
  // the failure mode we need to catch on a real production deploy, not silence.
  const isKnownStagingTarget = env.VITE_DEPLOY_TARGET?.trim().toLowerCase() === "staging";

  if (isKnownStagingTarget) {
    logger.warn(
      "[analytics] Firebase analytics env vars not set for this deploy target; analytics disabled",
      {
        deployTarget: env.VITE_DEPLOY_TARGET,
        missingKeys,
      }
    );
    return {
      missingKeys,
      adsTagId,
      warnsAboutMissingMeasurementForAds,
    };
  }

  logger.error(
    "[analytics] Missing required Firebase env vars for production build",
    {
      missingKeys,
      action:
        "Set these variables before build:release/deploy to avoid analytics outages.",
    }
  );

  return {
    missingKeys,
    adsTagId,
    warnsAboutMissingMeasurementForAds,
  };
}
